import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { ContentCopyOutlined, DownloadOutlined, EditOutlined, OpenInNewOutlined } from '@mui/icons-material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import CrpReportService from '../../../../shared/services/crp-report/crp-report.service'
import { ICrpFigures, ICrpReport } from '../../../../shared/services/crp-report/crp-report.model'
import FileDownloadService from '../../../../shared/services/file-download/file-download.service'
import { IFileDownload } from '../../../../shared/services/file-download/file-download.model'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { LoadingPage } from '../../loading-page'
import { CrpPlanDocument } from '../../../presentation-components/reporting-components/crp/crp-plan-document'

const DOWNLOADED_FILES_PATH = '/settings/company-settings/downloaded-files'

// One download button per generated file, in this order.
const ARTEFACTS: { type: string, label: string }[] = [
  { type: 'crpPdf', label: 'PDF' },
  { type: 'crpHtml', label: 'web page' },
  { type: 'crpXlsx', label: 'spreadsheet' },
]

const isProcessing = (artefact?: IFileDownload) =>
  artefact?.status === 'PENDING' || artefact?.status === 'IN_PROGRESS'

/**
 * Read-only view of a generated Carbon Reduction Plan: the document itself, a download button for each
 * generated file and the public-link controls. Editing and regenerating stay in the wizard, reached from
 * the Edit plan button. Drafts have nothing to view yet, so they redirect straight into the wizard.
 */
export const ReportingCrpDetailPage = () => {
  const crpReportService = useMemo(() => new CrpReportService(), [])
  const fileDownloadService = useMemo(() => new FileDownloadService(), [])
  const { id } = useParams()
  const navigate = useNavigate()
  const userDetailsContext = useContext(UserDetailsContext)
  const userNotificationsContext = useContext(UserNotificationsContext)

  const [report, setReport] = useState<ICrpReport | null>(null)
  const [figures, setFigures] = useState<ICrpFigures | null>(null)
  const [artefacts, setArtefacts] = useState<IFileDownload[]>([])
  const [artefactsFailedToLoad, setArtefactsFailedToLoad] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const showError = (message: string) => {
    userNotificationsContext.openSnackbar({ open: true, message, backgroundColor: 'text.primary' })
  }

  // The download buttons are secondary: a failure here falls back to a link to Downloaded Files.
  const loadArtefacts = useCallback(() => {
    if (!id) return
    fileDownloadService.getAllForCrpReport(id)
      .then((rows) => {
        setArtefacts(rows)
        setArtefactsFailedToLoad(false)
      })
      .catch((err) => {
        console.log(err)
        setArtefactsFailedToLoad(true)
      })
  }, [id, fileDownloadService])

  // The newest row of each type decides its button. Regenerating queues fresh rows, and the previous
  // finished file holds the previous figures, so it is not offered while its replacement is on the way.
  const latestByType = useMemo(() => {
    const byType = new Map<string, IFileDownload>()
    const newestFirst = [...artefacts]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    for (const artefact of newestFirst) {
      if (!byType.has(artefact.type)) byType.set(artefact.type, artefact)
    }
    return byType
  }, [artefacts])

  // A regenerate queues files that finish shortly after this page loads, so poll while any are still
  // processing rather than making the user refresh to see the finished PDF.
  useEffect(() => {
    const processing = ARTEFACTS.some(({ type }) => isProcessing(latestByType.get(type)))
    if (!processing) return
    const timer = setTimeout(loadArtefacts, 5000)
    return () => clearTimeout(timer)
  }, [latestByType, loadArtefacts])

  useEffect(() => {
    if (!id) return
    crpReportService.getOne(id)
      .then(async (response) => {
        if (response.status !== 'GENERATED') {
          // A draft has no frozen figures to show; resume the wizard instead.
          navigate(`/reporting/carbon-reduction-plan/${id}/edit`, { replace: true })
          return
        }
        setReport(response)
        setFigures(await crpReportService.getFigures(id))
        setIsLoading(false)
        loadArtefacts()
      })
      .catch((err) => {
        console.log(err)
        setIsLoading(false)
        showError('Could not load this Carbon Reduction Plan')
      })
  }, [id])

  const failedLabels = ARTEFACTS
    .filter(({ type }) => latestByType.get(type)?.status === 'FAILED')
    .map(({ label }) => label)

  const handleDownloadArtefact = (artefact: IFileDownload) => {
    fileDownloadService.downloadFile(artefact.id, artefact.filename)
      .catch((err) => {
        console.log(err)
        showError('Could not download the file, please try again')
      })
  }

  const publicUrl = report?.shareId
    ? `${window.location.origin}/public/carbon-reduction-plan/${report.shareId}`
    : null

  const handleShare = async () => {
    if (!report) return
    try {
      const response = await crpReportService.share(report.id)
      setReport({ ...report, shareId: response.shareId })
    } catch (err: any) {
      console.log(err)
      showError(err?.response?.data?.message || 'Could not create a public link')
    }
  }

  const handleUnshare = async () => {
    if (!report) return
    const confirmed = await userNotificationsContext.openDialog({
      active: true,
      title: 'Remove public link',
      message: 'Anyone using the current link will no longer be able to view this plan. Continue?',
      buttonCopy: 'Remove link',
    })
    if (!confirmed) return
    try {
      await crpReportService.unshare(report.id)
      setReport({ ...report, shareId: null })
    } catch (err) {
      console.log(err)
      showError('Could not remove the public link')
    }
  }

  const handleCopyPublicUrl = () => {
    if (!publicUrl) return
    navigator.clipboard.writeText(publicUrl)
      .then(() => userNotificationsContext.openSnackbar({ open: true, message: 'Link copied', backgroundColor: 'text.primary' }))
      .catch(() => showError('Could not copy the link'))
  }

  const isProgress = report?.planType === 'PROGRESS'

  return (
    <PageOuterContainer>
      <PageHeading
        title={isProgress ? 'Carbon Reduction Progress Report' : 'Carbon Reduction Plan (PPN 006)'}
        includeBackButton={true}
        backButtonLink='/reporting/carbon-reduction-plan'
        backButtonText='Back to plans'
        hasTabs={false}
        showChip={!!report}
        chipColor='primary'
        chipLabel='Generated'
        extraActions={report && (
          <Button
            variant='outlined'
            startIcon={<EditOutlined sx={{ fontSize: 'small' }} />}
            onClick={() => navigate(`/reporting/carbon-reduction-plan/${report.id}/edit`)}
            sx={{ textTransform: 'none' }}
          >
            <Typography sx={{ pl: 0.5 }}>Edit plan</Typography>
          </Button>
        )}
      />
      <LoadingPage pageReady={!isLoading}>
        {report && figures && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: 880 }}>
              <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
                {report.generatedAt
                  ? `Last generated ${moment(report.generatedAt).format('DD/MM/YYYY [at] HH:mm')}. `
                  : ''}
                Download the PDF, web page and spreadsheet below. To change the figures or narratives,
                edit the plan and regenerate it. A public link is optional: PPN 006 still requires you to
                publish the plan on your own website.
              </Typography>
              <Stack direction='row' flexWrap='wrap' useFlexGap spacing={1} sx={{ mb: failedLabels.length ? 1 : 2 }}>
                {ARTEFACTS.map(({ type, label }) => {
                  const artefact = latestByType.get(type)
                  if (artefact?.status === 'COMPLETED') {
                    return (
                      <Button
                        key={type}
                        variant='outlined'
                        onClick={() => handleDownloadArtefact(artefact)}
                        startIcon={<DownloadOutlined sx={{ fontSize: 'small' }} />}
                        sx={{ textTransform: 'none' }}
                      >
                        <Typography sx={{ pl: 0.5 }}>Download {label}</Typography>
                      </Button>
                    )
                  }
                  if (isProcessing(artefact)) {
                    return (
                      <Button
                        key={type}
                        variant='outlined'
                        disabled
                        startIcon={<CircularProgress size={14} />}
                        sx={{ textTransform: 'none' }}
                      >
                        <Typography sx={{ pl: 0.5 }}>Preparing {label}…</Typography>
                      </Button>
                    )
                  }
                  return null
                })}
                {artefactsFailedToLoad && (
                  <Button
                    variant='outlined'
                    component='a'
                    href={DOWNLOADED_FILES_PATH}
                    target='_blank'
                    rel='noopener noreferrer'
                    startIcon={<OpenInNewOutlined sx={{ fontSize: 'small' }} />}
                    sx={{ textTransform: 'none' }}
                  >
                    <Typography sx={{ pl: 0.5 }}>View downloaded files</Typography>
                  </Button>
                )}
                {!publicUrl && (
                  <Button variant='outlined' onClick={handleShare} sx={{ textTransform: 'none' }}>
                    <Typography>Create public link</Typography>
                  </Button>
                )}
              </Stack>
              {failedLabels.length > 0 && (
                <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
                  The {failedLabels.length > 1
                    ? `${failedLabels.slice(0, -1).join(', ')} and ${failedLabels[failedLabels.length - 1]}`
                    : failedLabels[0]} could not be produced. Edit the plan and regenerate it to
                  try again.
                </Typography>
              )}

              {publicUrl && (
                <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
                  <Typography variant='subtitle2' sx={{ mb: 0.5 }}>Public link</Typography>
                  <Typography variant='body2' sx={{ color: 'text.secondary', mb: 1 }}>
                    Anyone with this address can view the plan without signing in. It shows the figures
                    exactly as generated.
                  </Typography>
                  <Typography variant='body2' sx={{ wordBreak: 'break-all', mb: 1.5 }}>{publicUrl}</Typography>
                  <Stack direction='row' spacing={1}>
                    <Button
                      variant='outlined'
                      startIcon={<ContentCopyOutlined sx={{ fontSize: 'small' }} />}
                      onClick={handleCopyPublicUrl}
                      sx={{ textTransform: 'none' }}
                    >
                      <Typography sx={{ pl: 0.5 }}>Copy link</Typography>
                    </Button>
                    <Button variant='outlined' onClick={handleUnshare} sx={{ textTransform: 'none' }}>
                      <Typography>Remove public link</Typography>
                    </Button>
                  </Stack>
                </Paper>
              )}

              <CrpPlanDocument
                plan={{
                  organisationName: userDetailsContext.userDetails?.organisation?.name || '',
                  organisationLogoUrl: userDetailsContext.userDetails?.organisation?.logoFilepath || null,
                  planType: report.planType,
                  publicationDate: report.publicationDate,
                  generatedAt: report.generatedAt,
                  commitmentText: report.commitmentText,
                  targetsNarrative: report.targetsNarrative,
                  completedMeasuresNarrative: report.completedMeasuresNarrative,
                  plannedMeasuresNarrative: report.plannedMeasuresNarrative,
                  baselineAdditionalDetails: report.baselineAdditionalDetails,
                  currentAdditionalDetails: report.currentAdditionalDetails,
                  progressNarrative: report.progressNarrative,
                  signOffName: report.signOffName,
                  signOffTitle: report.signOffTitle,
                  signOffDate: report.signOffDate,
                  figures,
                  declarationParagraphs: figures.declarationParagraphs || [],
                  disclaimerText: figures.disclaimerText,
                }}
              />
            </Box>
          </Box>
        )}
      </LoadingPage>
    </PageOuterContainer>
  )
}
